import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Phone } from '@phosphor-icons/react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter26 D — Confirmation card shown in /my-car right after the customer creates
 * a premium booking. Disappears automatically once the manager marks the booking
 * as 'completed' / 'cancelled' / 'no_show'.
 */
export default function MyPremiumBookingsCard() {
  const { t, language } = useLanguage();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/bookings`, { withCredentials: true });
        if (!alive) return;
        // Only show active premium bookings (not yet completed/cancelled).
        const visible = (r.data || []).filter((b) => (
          ['pending', 'confirmed', 'in_progress'].includes(b.status)
        ));
        setRows(visible);
      } catch (err) { if (err?.response?.status !== 401) console.warn('[MyBookings] load failed', err?.response?.status || err?.message); }
    };
    load();
    return () => { alive = false; };
  }, []);

  if (rows.length === 0) return null;

  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(language === 'sq' ? 'sq-AL' : language, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="px-4 mb-4 space-y-3" data-testid="my-premium-bookings">
      {rows.map((b) => (
        <motion.div key={b.booking_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30"
                    data-testid={`booking-confirm-${b.booking_id}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
              <CheckCircle size={22} weight="fill" className="text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-emerald-300" data-testid="booking-confirm-title">
                Rezervimi juaj është konfirmuar
              </p>
              <p className="text-sm text-white mt-1 truncate" data-testid="booking-confirm-service">
                {b.service_name_snapshot}
              </p>
              <p className="text-xs text-white/60 mt-1 flex items-center gap-1.5" data-testid="booking-confirm-when">
                <Calendar size={12} weight="duotone" /> {fmt(b.scheduled_at)}
              </p>
              <p className="text-[11px] text-white/50 mt-2 flex items-center gap-1.5">
                <Phone size={11} weight="duotone" /> Ju do të njoftoheni nga konsulenti ynë
              </p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
